/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-refresh/only-export-components */
// src\context\AuthContext.tsx - FIXED FOR 304 RESPONSE HANDLING
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";

// Helper functions for account lock status
export const checkAccountLockStatus = (): {
  locked: boolean;
  retryAfter?: number;
  message?: string;
} => {
  const lockUntil = localStorage.getItem("accountLockedUntil");
  const remainingAttempts = localStorage.getItem("remainingAttempts");

  // Check for active lock
  if (lockUntil) {
    const lockTime = new Date(lockUntil).getTime();
    const now = Date.now();

    if (lockTime > now) {
      const retryAfter = Math.ceil((lockTime - now) / 1000);
      return {
        locked: true,
        retryAfter,
        message: `Account temporarily locked. Try again in ${formatTimeRemaining(retryAfter)}.`,
      };
    } else {
      // Clear expired lock
      localStorage.removeItem("accountLockedUntil");
    }
  }

  // Check for remaining attempts
  if (remainingAttempts) {
    const attempts = parseInt(remainingAttempts);
    if (attempts < 5) {
      return {
        locked: false,
        message: `${attempts} attempt${attempts !== 1 ? "s" : ""} remaining before account lock.`,
      };
    }
    localStorage.removeItem("remainingAttempts");
  }

  return { locked: false };
};

export const formatTimeRemaining = (seconds: number): string => {
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  }
  return `${hours} hour${hours !== 1 ? "s" : ""} ${remainingMinutes} minute${remainingMinutes !== 1 ? "s" : ""}`;
};

export const clearLockStorage = (): void => {
  localStorage.removeItem("accountLockedUntil");
  localStorage.removeItem("remainingAttempts");
};

// NEW: Debounce utility
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// NEW: Rate limiter utility
class RateLimiter {
  private lastCall: number = 0;
  private minInterval: number;

  constructor(minIntervalMs: number = 2000) {
    this.minInterval = minIntervalMs;
  }

  canMakeRequest(): boolean {
    const now = Date.now();
    if (now - this.lastCall >= this.minInterval) {
      this.lastCall = now;
      return true;
    }
    return false;
  }

  reset() {
    this.lastCall = 0;
  }
}

// Types
interface LockStatus {
  locked: boolean;
  retryAfter?: number;
  message?: string;
}

interface AuthError {
  message: string;
  code?: string;
  retryAfter?: number;
  remainingAttempts?: number;
  lockUntil?: string;
}

interface UserProfile {
  phone?: string;
  location?: string;
  bio?: string;
  avatar?: string;
}

// Enhanced Subscription interface
interface Subscription {
  package: any;
  planCode: ReactNode;
  endDate: string | number | Date;
  id: string;
  status: string;
  planId: number;
  planName?: string;
  planType: "whitelist" | "job_posting" | "hybrid";
  isActive: boolean;
  expiresAt: string;
  billingPeriod?: "monthly" | "annual";
  createdAt: string;
  packageType?: string;
  features?: string[];
  jobsPosted?: number;
  maxJobPosts?: number;
}

interface Job {
  job: any;
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  description: string;
  category: string;
  deadline?: string;
  createdAt: string;
  postedBy: {
    id: string;
    name: string;
    email: string;
  };
  _count?: {
    jobApplications: number;
    jobBookmarks: number;
  };
}

interface Bookmark {
  id: string;
  jobId: string;
  userId: string;
  createdAt: string;
  job: Job;
}

// Enhanced User interface with comprehensive subscription state AND preferences
interface UserPreferences {
  notifications?: boolean;
  darkMode?: boolean;
  emailUpdates?: boolean;
  jobAlerts?: boolean;
  securityAlerts?: boolean;
  loginAlerts?: boolean;
  suspiciousActivityAlerts?: boolean;
  passwordChangeAlerts?: boolean;
  twoFactorAlerts?: boolean;
}

// Enhanced User interface with comprehensive subscription state
interface User {
  firstName: any;
  lastName: any;
  profile: UserProfile | undefined;
  id: string;
  email: string;
  name: string;
  role: "admin" | "user" | "super_admin";
  isWhitelisted: boolean;
  subscribed: boolean;
  token: string;
  createdAt: string;
  profiles?: UserProfile;
  emailVerified?: boolean;
  active?: boolean;
  deactivatedAt?: string;
  deactivatedBy?: string;
  deactivationType?: "user_initiated" | "admin" | "super_admin_action";
  subscription?: Subscription;
  whitelistSubscription?: Subscription;
  jobPostingSubscription?: Subscription;
  canPostJobs?: string[];
  capabilities?: string[];
  preferences?: UserPreferences; // UPDATED: Now uses UserPreferences interface
  activePackageType?: string;
  subscriptionState?: {
    user: {
      subscribed: boolean;
      activePackageType: string;
      role: string;
    };
    subscriptions: {
      active: Subscription[];
      hasJobPosting: boolean;
      hasWhitelist: boolean;
      canPostJobs: boolean;
      isWhitelisted: boolean;
    };
    whitelist: {
      isActive: boolean;
      expiresAt: string;
      createdAt: string;
    } | null;
    capabilities: {
      canPostJobs: boolean;
      canApplyJobs: boolean;
      canUseWhitelist: boolean;
      maxJobPosts: number;
      remainingJobPosts: number;
    };
  };
}

interface LoginResult {
  success: boolean;
  error?: any;
  locked?: boolean;
  retryAfter?: number;
  remainingAttempts?: number;
  requiresVerification?: boolean;
  accountDeactivated?: boolean;
  deactivatedAt?: string;
  deactivationType?: string;
  canBeReactivated?: boolean;
}

interface AuthContextType {
  user: User | null;
  bookmarks: Bookmark[];
  myJobs: Job[];
  login: (
    email: string,
    password: string,
    headers?: Record<string, string>,
  ) => Promise<LoginResult>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  addBookmark: (bookmark: Bookmark) => void;
  removeBookmark: (jobId: string) => void;
  fetchBookmarks: () => Promise<void>;
  isJobBookmarked: (jobId: string) => boolean;
  // Enhanced bookmark functions
  getBookmarkStats: () => { total: number; recent: number; jobIds: string[] };
  clearAllBookmarks: () => void;
  syncBookmarksToServer: () => Promise<boolean>;
  // Job management functions
  fetchMyJobs: () => Promise<void>;
  addJob: (job: Job) => void;
  updateJob: (jobId: string, jobData: Partial<Job>) => void;
  removeJob: (jobId: string) => void;
  getJobStats: () => { total: number; active: number; draft: number };
  clearAllJobs: () => void;
  syncJobsToServer: () => Promise<boolean>;
  // Password reset functions
  requestPasswordReset: (email: string) => Promise<void>;
  verifyResetToken: (token: string) => Promise<boolean>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  // Profile management
  updateProfile: (profileData: {
    name: string;
    email: string;
    phone?: string;
    location?: string;
    bio?: string;
  }) => Promise<void>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<void>;
  // Enhanced subscription management
  updateSubscription: (subscription: Subscription) => void;
  updateWhitelistStatus: (isWhitelisted: boolean) => void;
  setJobPostingSubscription: (subscription: Subscription | null) => void;
  setWhitelistSubscription: (subscription: Subscription | null) => void;
  // Subscription state management APIs
  fetchSubscriptionState: () => Promise<void>;
  syncSubscriptionState: () => Promise<void>;
  getSubscriptionBenefits: () => Promise<any>;
  trackJobPostingUsage: (jobId: string, action?: string) => Promise<any>;
  // Helper methods
  hasActiveJobPostingSubscription: () => boolean;
  hasActiveWhitelistSubscription: () => boolean;
  canPostJobs: () => boolean;
  getRemainingJobPosts: () => number;
  // Account management
  deleteAccount: (password: string) => Promise<void>;
  deactivateAccount: (password: string) => Promise<void>;
  // NEW: Account reactivation functions
  requestAccountReactivation: (email: string) => Promise<void>;
  completeAccountReactivation: (
    token: string,
    newPassword: string,
  ) => Promise<void>;
  // NEW: Account status check
  checkAccountStatus: (email: string) => Promise<{
    deactivated: boolean;
    deactivatedAt?: string;
    deactivationType?: string;
    canBeReactivated?: boolean;
    exists?: boolean;
    active?: boolean;
    reason?: string;
  }>;
  // NEW: Email verification
  resendVerificationEmail: (email: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<boolean>;
  // UPDATED: Profile update function with enhanced error handling
  updateUserProfile: (profileData: {
    name?: string;
    email?: string;
    phone?: string;
    location?: string;
    bio?: string;
    avatar?: string;
  }) => Promise<{ success: boolean; message?: string }>;
  // NEW: User status checking function
  checkAndRefreshUserStatus: () => Promise<{
    isValid: boolean;
    needsReauth?: boolean;
    user?: User;
    message?: string;
  }>;
  // Dark mode
  isDark: boolean;
  toggleDarkMode: (enabled: boolean) => void;
  // Loading states
  loading: boolean;
  error: AuthError | null;
  clearError: () => void;
  // Rate limiting state
  rateLimited: boolean;
  retryAfter: number;
  // Token validation
  isTokenValid: () => boolean;
  // Check if user is authenticated
  isAuthenticated: boolean;
  // Token refresh
  refreshToken: () => Promise<boolean>;
  // Account lock status management
  lockStatus: LockStatus;
  checkLockStatus: () => LockStatus;
  clearLockStatus: () => void;
  startCountdown: (seconds: number) => void;
  countdown: number | null;
  updateLockStatus: (status: LockStatus) => void;
  // NEW: Account status management
  isAccountActive: () => boolean;
  // NEW: Admin functions
  adminDeactivateUser: (userId: string) => Promise<void>;
  adminReactivateUser: (userId: string) => Promise<void>;
  // NEW: Super admin functions
  promoteToSuperAdmin: (userId: string) => Promise<void>;
  adminDeactivateAdmin: (userId: string) => Promise<void>;
  // NEW: User refresh function
  refreshUser: () => Promise<void>;
  // NEW: API request function
  apiRequest: <T = any>(
    endpoint: string,
    options?: RequestInit,
    retryCount?: number,
  ) => Promise<T>;
  // NEW: Preferences management
  updatePreferences: (preferences: UserPreferences) => Promise<void>;
  getPreferences: () => UserPreferences;
  savePreferencesToServer: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Use environment variable for API base URL
const API_BASE = import.meta.env.VITE_API_BASE;

// Enhanced subscription synchronization utility
const syncUserSubscriptions = async (
  userId: string,
  token: string,
): Promise<any> => {
  try {
    console.log("🔄 Syncing user subscriptions for user:", userId);
    const response = await fetch(`${API_BASE}/payments/subscription/state`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Subscription sync successful:", data);

      // Update local storage with latest subscription data
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        const updatedUser = {
          ...user,
          ...data.user,
          subscriptionState: data,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }

      return data;
    } else {
      throw new Error(
        `Subscription sync failed with status: ${response.status}`,
      );
    }
  } catch (error) {
    console.error("❌ Failed to sync subscriptions:", error);
    throw error;
  }
};

// Helper to check subscription status
const getSubscriptionStatus = (user: User | null) => {
  if (!user) return { canPostJobs: false, isWhitelisted: false };

  // Use subscription state if available
  if (user.subscriptionState) {
    return {
      canPostJobs: user.subscriptionState.capabilities.canPostJobs,
      isWhitelisted: user.subscriptionState.subscriptions.isWhitelisted,
      remainingJobPosts: user.subscriptionState.capabilities.remainingJobPosts,
      maxJobPosts: user.subscriptionState.capabilities.maxJobPosts,
    };
  }

  // Fallback to legacy logic
  const canPostJobs =
    user.role === "admin" ||
    user.isWhitelisted ||
    user.jobPostingSubscription?.isActive ||
    user.subscribed;

  const isWhitelisted =
    user.isWhitelisted || !!user.whitelistSubscription?.isActive;

  return { canPostJobs, isWhitelisted };
};

// Type guard for API errors
interface ApiError {
  code: string;
  error: string;
  retryAfter?: number;
  remainingAttempts?: number;
  lockUntil?: string;
  deactivatedAt?: string;
  deactivationType?: string;
}

const isApiError = (error: any): error is ApiError => {
  return error && typeof error === "object" && "code" in error;
};

// Helper to determine if an error is an authentication error (should logout) vs authorization error (should not logout)
const isAuthenticationError = (error: any): boolean => {
  if (!error) return false;

  const errorMessage = error.message?.toLowerCase() || "";
  const errorCode = error.code?.toLowerCase() || "";

  // Authentication errors that should trigger logout
  const authErrorIndicators = [
    "token",
    "session",
    "authenticate",
    "unauthorized",
    "expired",
    "invalid",
    "missing_token",
    "token_expired",
    "invalid_token",
    "authentication_failed",
  ];

  // Authorization errors that should NOT trigger logout
  const authZErrorIndicators = [
    "permission",
    "forbidden",
    "access_denied",
    "insufficient_permissions",
    "not_authorized",
  ];

  // Check if it's specifically an authorization error
  const isAuthZError = authZErrorIndicators.some(
    (indicator) =>
      errorMessage.includes(indicator) || errorCode.includes(indicator),
  );

  // If it's an authorization error, don't treat it as authentication error
  if (isAuthZError) {
    return false;
  }

  // Check for authentication errors
  return authErrorIndicators.some(
    (indicator) =>
      errorMessage.includes(indicator) || errorCode.includes(indicator),
  );
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [myJobs, setMyJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<AuthError | null>(null);
  const [rateLimited, setRateLimited] = useState<boolean>(false);
  const [retryAfter, setRetryAfter] = useState<number>(0);
  const [isDark, setIsDark] = useState<boolean>(false);

  // Account lock status state
  const [lockStatus, setLockStatus] = useState<LockStatus>({ locked: false });
  const [countdown, setCountdown] = useState<number | null>(null);

  // NEW: Track sync status to prevent concurrent syncs
  const syncInProgress = useRef(false);
  const lastSyncTime = useRef<number>(0);
  const bookmarkRateLimiter = useRef(new RateLimiter(5000)); // 5 seconds between syncs
  const jobRateLimiter = useRef(new RateLimiter(5000));

  const clearError = () => setError(null);

  // Initialize dark mode from user preferences
  useEffect(() => {
    const savedPreferences = localStorage.getItem("userPreferences");
    if (savedPreferences) {
      const preferences = JSON.parse(savedPreferences);
      setIsDark(preferences.darkMode || false);

      // Apply dark mode class
      if (preferences.darkMode) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    }
  }, []);

  // Check lock status on component mount
  useEffect(() => {
    const status = checkAccountLockStatus();
    setLockStatus(status);

    if (status.locked && status.retryAfter) {
      startCountdown(status.retryAfter);
    }
  }, []);

  // Function to manually check lock status
  const checkLockStatus = (): LockStatus => {
    const status = checkAccountLockStatus();
    setLockStatus(status);
    return status;
  };

  // Function to clear lock status
  const clearLockStatus = (): void => {
    clearLockStorage();
    setLockStatus({ locked: false });
    setCountdown(null);
  };

  // Countdown timer for locked accounts
  const startCountdown = (seconds: number) => {
    setCountdown(seconds);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          clearLockStatus(); // Clear when countdown finishes
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  };

  // Update lock status (to be called from login function)
  const updateLockStatus = (status: LockStatus) => {
    setLockStatus(status);
    if (status.locked && status.retryAfter) {
      startCountdown(status.retryAfter);
    }
  };

  // NEW: Account status management functions
  const isAccountActive = (): boolean => {
    return user?.active !== false; // Default to true if undefined
  };

  // NEW: Check account status before login
  const checkAccountStatus = async (
    email: string,
  ): Promise<{
    deactivated: boolean;
    deactivatedAt?: string;
    deactivationType?: string;
    canBeReactivated?: boolean;
    exists?: boolean;
    active?: boolean;
    reason?: string;
  }> => {
    try {
      console.log("🔍 Checking account status for:", email);

      const response = await apiRequest<{
        deactivated: boolean;
        deactivatedAt?: string;
        deactivationType?: string;
        canBeReactivated?: boolean;
        exists?: boolean;
        active?: boolean;
        reason?: string;
      }>("/auth/check-account-status", {
        method: "POST",
        body: JSON.stringify({ email }),
      });

      console.log("📊 Account status response:", response);
      return response;
    } catch (error) {
      console.error("❌ Account status check failed:", error);

      // Return more detailed error information
      return {
        deactivated: false,
        exists: false,
        active: false,
      };
    }
  };

  // NEW: Check and refresh user status for authenticated users
  const checkAndRefreshUserStatus = async (): Promise<{
    isValid: boolean;
    needsReauth?: boolean;
    user?: User;
    message?: string;
  }> => {
    try {
      console.log("🔄 Checking authenticated user status...");

      // This uses the NEW endpoint /auth/check-status (GET)
      const response = await apiRequest<{
        user: User;
        needsTokenRefresh: boolean;
        isActive: boolean;
        status: string;
      }>("/auth/check-status"); // GET request, no body needed

      console.log("📊 Authenticated user status:", response);

      if (!response.isActive) {
        console.log("❌ User account is not active");
        return {
          isValid: false,
          needsReauth: true,
          message: "Your account is not active. Please contact support.",
        };
      }

      if (response.needsTokenRefresh) {
        console.log("🔄 Token needs refresh, attempting refresh...");

        // Try to refresh token first before logging out
        const tokenRefreshed = await refreshToken();
        if (!tokenRefreshed) {
          console.log("❌ Token refresh failed, logging out...");
          logout();
          return {
            isValid: false,
            needsReauth: true,
            message: "Session expired. Please log in again.",
          };
        }

        console.log("✅ Token refreshed successfully");
        return {
          isValid: true,
          user: response.user,
        };
      }

      // Update user data if email changed (after reactivation)
      if (response.user.email !== user?.email) {
        console.log("✅ Updating user data after reactivation/change");
        console.log(
          "Old email:",
          user?.email,
          "New email:",
          response.user.email,
        );
        updateUser(response.user);
      }

      return {
        isValid: true,
        user: response.user,
      };
    } catch (error: any) {
      console.error("❌ Failed to check user status:", error);

      // Handle specific error cases
      if (
        error?.message?.includes("deactivated") ||
        error?.code === "USER_NOT_FOUND_OR_DEACTIVATED"
      ) {
        return {
          isValid: false,
          needsReauth: true,
          message: "Account deactivated. Please request reactivation.",
        };
      }

      if (
        error?.message?.includes("token") ||
        error?.message?.includes("unauthorized")
      ) {
        return {
          isValid: false,
          needsReauth: true,
          message: "Session invalid. Please log in again.",
        };
      }

      // For network errors, assume temporary issue and allow retry
      if (
        error instanceof TypeError &&
        error.message.includes("Failed to fetch")
      ) {
        console.warn("⚠️ Network error during status check, allowing retry");
        return {
          isValid: true, // Assume valid to allow retry
          message: "Network issue detected. Please check your connection.",
        };
      }

      return {
        isValid: false,
        needsReauth: false,
        message: "Unable to verify account status. Please try again.",
      };
    }
  };

  // ==================== PREFERENCES MANAGEMENT ====================

  // NEW: Get user preferences
  const getPreferences = (): UserPreferences => {
    const savedPreferences = localStorage.getItem("userPreferences");
    if (savedPreferences) {
      return JSON.parse(savedPreferences);
    }

    // Return default preferences
    return {
      notifications: true,
      darkMode: isDark,
      emailUpdates: true,
      jobAlerts: true,
      securityAlerts: true,
      loginAlerts: true,
      suspiciousActivityAlerts: true,
      passwordChangeAlerts: true,
      twoFactorAlerts: true,
    };
  };

  // NEW: Update preferences
  const updatePreferences = async (
    preferences: UserPreferences,
  ): Promise<void> => {
    setLoading(true);
    clearError();

    try {
      // Update local storage
      localStorage.setItem("userPreferences", JSON.stringify(preferences));

      // Update user state if logged in
      if (user) {
        setUser((prev) => (prev ? { ...prev, preferences } : null));

        // Update localStorage user data
        const userData = localStorage.getItem("user");
        if (userData) {
          const parsedUser = JSON.parse(userData);
          const updatedUser = { ...parsedUser, preferences };
          localStorage.setItem("user", JSON.stringify(updatedUser));
        }
      }

      // Handle dark mode toggle
      if (preferences.darkMode !== undefined) {
        toggleDarkMode(preferences.darkMode);
      }
    } catch (error: unknown) {
      handleError(error, "Failed to update preferences");
    } finally {
      setLoading(false);
    }
  };

  // NEW: Save preferences to server
  const savePreferencesToServer = async (): Promise<void> => {
    if (!user) return;

    setLoading(true);
    clearError();

    try {
      const preferences = getPreferences();
      await apiRequest("/auth/preferences", {
        method: "PUT",
        body: JSON.stringify({ preferences }),
      });
    } catch (error: unknown) {
      console.error("Failed to save preferences to server:", error);
      // Don't throw error for preference saving failures
    } finally {
      setLoading(false);
    }
  };

  // ==================== OPTIMIZED BOOKMARK MANAGEMENT ====================

  // NEW: Optimized bookmark sync with rate limiting
  const syncBookmarksToServer = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    // Prevent concurrent syncs
    if (syncInProgress.current) {
      console.log("⏸️ Bookmark sync already in progress, skipping...");
      return false;
    }

    // Rate limit check
    if (!bookmarkRateLimiter.current.canMakeRequest()) {
      console.log("⏸️ Bookmark sync rate limited, skipping...");
      return false;
    }

    syncInProgress.current = true;

    try {
      const localBookmarks = localStorage.getItem("userBookmarks");
      if (!localBookmarks) {
        syncInProgress.current = false;
        return true;
      }

      const bookmarksToSync = JSON.parse(localBookmarks);

      if (bookmarksToSync.length > 0) {
        console.log("🔄 Syncing bookmarks to server:", bookmarksToSync.length);

        await apiRequest("/jobs/bookmarks/sync", {
          method: "POST",
          body: JSON.stringify({ bookmarks: bookmarksToSync }),
        });

        console.log("✅ Bookmarks synced to server successfully");
        lastSyncTime.current = Date.now();
      }

      syncInProgress.current = false;
      return true;
    } catch (error: any) {
      syncInProgress.current = false;

      // Handle rate limiting
      if (
        error?.message?.includes("429") ||
        error?.message?.includes("rate limit")
      ) {
        console.warn("⚠️ Bookmark sync rate limited by server");
        setRateLimited(true);

        // Extract retry-after from error
        const retryMatch = error.message.match(/(\d+)\s*(second|minute|hour)/i);
        if (retryMatch) {
          const value = parseInt(retryMatch[1]);
          const unit = retryMatch[2].toLowerCase();
          const seconds =
            unit === "minute"
              ? value * 60
              : unit === "hour"
                ? value * 3600
                : value;
          setRetryAfter(seconds);
        }

        return false;
      }

      console.error("❌ Failed to sync bookmarks to server:", error);
      return false;
    }
  }, [user]);

  // NEW: Debounced version for automatic syncing
  const debouncedBookmarkSync = useCallback(
    debounce(syncBookmarksToServer, 3000), // Wait 3 seconds after last change
    [syncBookmarksToServer],
  );

  // MODIFIED: Bookmark functions now use debounced sync
  const addBookmark = useCallback(
    (bookmark: Bookmark) => {
      setBookmarks((prev) => {
        if (prev.some((b) => b.jobId === bookmark.jobId)) {
          return prev;
        }

        const updatedBookmarks = [...prev, bookmark];
        localStorage.setItem("userBookmarks", JSON.stringify(updatedBookmarks));
        console.log(
          "✅ Bookmark added locally. Total:",
          updatedBookmarks.length,
        );

        // Trigger debounced sync (won't execute until 3s after last change)
        debouncedBookmarkSync();

        return updatedBookmarks;
      });
    },
    [debouncedBookmarkSync],
  );

  const removeBookmark = useCallback(
    (jobId: string) => {
      setBookmarks((prev) => {
        const updatedBookmarks = prev.filter((b) => b.jobId !== jobId);
        localStorage.setItem("userBookmarks", JSON.stringify(updatedBookmarks));
        console.log(
          "🗑️ Bookmark removed locally. Total:",
          updatedBookmarks.length,
        );

        // Trigger debounced sync
        debouncedBookmarkSync();

        return updatedBookmarks;
      });
    },
    [debouncedBookmarkSync],
  );

  const isJobBookmarked = (jobId: string): boolean => {
    return bookmarks.some((bookmark) => bookmark.jobId === jobId);
  };

  // FIXED: Fetch bookmarks with proper 304 handling
  const fetchBookmarks = useCallback(async (): Promise<void> => {
    if (!user) return;

    // Don't fetch if we just synced
    const timeSinceLastSync = Date.now() - lastSyncTime.current;
    if (timeSinceLastSync < 10000) {
      console.log("⏸️ Skipping bookmark fetch (recently synced)");
      return;
    }

    try {
      console.log("📚 Fetching bookmarks from server...");
      const data = await apiRequest("/jobs/bookmarks/my-bookmarks");

      // Handle 304 response - use cached data
      if (data?.fromCache) {
        console.log("🔄 Using cached bookmarks data");
        // The data is already in localStorage from previous requests
        const localBookmarks = localStorage.getItem("userBookmarks");
        if (localBookmarks) {
          try {
            const parsedBookmarks = JSON.parse(localBookmarks);
            setBookmarks(Array.isArray(parsedBookmarks) ? parsedBookmarks : []);
            console.log("📋 Using cached bookmarks:", parsedBookmarks.length);
            return;
          } catch (parseError) {
            console.error("❌ Error parsing cached bookmarks:", parseError);
          }
        }
      }

      // FIX: Properly extract bookmarks from response structure
      let bookmarksData = [];

      if (data?.data?.bookmarks) {
        // New structure: data.data.bookmarks
        bookmarksData = data.data.bookmarks;
      } else if (data?.bookmarks) {
        // Alternative structure: data.bookmarks
        bookmarksData = data.bookmarks;
      } else if (Array.isArray(data)) {
        // Direct array
        bookmarksData = data;
      }

      const serverBookmarks = Array.isArray(bookmarksData) ? bookmarksData : [];

      console.log("📊 Extracted bookmarks:", serverBookmarks.length);
      setBookmarks(serverBookmarks);
      localStorage.setItem("userBookmarks", JSON.stringify(serverBookmarks));
      lastSyncTime.current = Date.now();

      console.log("✅ Bookmarks fetched and saved:", serverBookmarks.length);
    } catch (err: any) {
      console.error("❌ Failed to fetch bookmarks:", err);

      // Enhanced error handling for 304
      if (err.message?.includes("304")) {
        console.log("🔄 304 - Using locally cached bookmarks");
        const localBookmarks = localStorage.getItem("userBookmarks");
        if (localBookmarks) {
          try {
            const parsedBookmarks = JSON.parse(localBookmarks);
            setBookmarks(Array.isArray(parsedBookmarks) ? parsedBookmarks : []);
            return;
          } catch (parseError) {
            console.error("❌ Error parsing fallback bookmarks:", parseError);
          }
        }
      }

      // Fallback to localStorage on error
      const localBookmarks = localStorage.getItem("userBookmarks");
      if (localBookmarks) {
        try {
          const parsedBookmarks = JSON.parse(localBookmarks);
          setBookmarks(Array.isArray(parsedBookmarks) ? parsedBookmarks : []);
          console.log("📋 Using local bookmarks as fallback");
        } catch (parseError) {
          console.error("❌ Error parsing fallback bookmarks:", parseError);
          setBookmarks([]);
        }
      }
    }
  }, [user]);

  // NEW: Get bookmark statistics
  const getBookmarkStats = () => {
    return {
      total: bookmarks.length,
      recent: bookmarks.filter((bookmark) => {
        const bookmarkDate = new Date(bookmark.createdAt);
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return bookmarkDate > sevenDaysAgo;
      }).length,
      jobIds: bookmarks.map((bookmark) => bookmark.jobId),
    };
  };

  // NEW: Clear all bookmarks (useful for logout or account cleanup)
  const clearAllBookmarks = () => {
    setBookmarks([]);
    localStorage.removeItem("userBookmarks");
    console.log("🧹 All bookmarks cleared from localStorage");
  };

  // ==================== OPTIMIZED JOB MANAGEMENT ====================

  // NEW: Optimized job sync with rate limiting
  const syncJobsToServer = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    if (syncInProgress.current) {
      console.log("⏸️ Job sync already in progress, skipping...");
      return false;
    }

    if (!jobRateLimiter.current.canMakeRequest()) {
      console.log("⏸️ Job sync rate limited, skipping...");
      return false;
    }

    syncInProgress.current = true;

    try {
      const localJobs = localStorage.getItem("userJobs");
      if (!localJobs) {
        syncInProgress.current = false;
        return true;
      }

      const jobsToSync = JSON.parse(localJobs);

      if (jobsToSync.length > 0) {
        console.log("🔄 Syncing jobs to server:", jobsToSync.length);

        await apiRequest("/jobs/sync", {
          method: "POST",
          body: JSON.stringify({ jobs: jobsToSync }),
        });

        console.log("✅ Jobs synced to server successfully");
      }

      syncInProgress.current = false;
      return true;
    } catch (error: any) {
      syncInProgress.current = false;

      if (
        error?.message?.includes("429") ||
        error?.message?.includes("rate limit")
      ) {
        console.warn("⚠️ Job sync rate limited by server");
        return false;
      }

      console.error("❌ Failed to sync jobs to server:", error);
      return false;
    }
  }, [user]);

  const debouncedJobSync = useCallback(debounce(syncJobsToServer, 3000), [
    syncJobsToServer,
  ]);

  const addJob = useCallback(
    (job: Job) => {
      setMyJobs((prev) => {
        if (prev.some((j) => j.id === job.id)) {
          return prev;
        }

        const updatedJobs = [...prev, job];
        localStorage.setItem("userJobs", JSON.stringify(updatedJobs));
        console.log("✅ Job added locally. Total:", updatedJobs.length);

        debouncedJobSync();

        return updatedJobs;
      });
    },
    [debouncedJobSync],
  );

  const updateJob = (jobId: string, jobData: Partial<Job>) => {
    setMyJobs((prev) => {
      const updatedJobs = prev.map((job) =>
        job.id === jobId ? { ...job, ...jobData } : job,
      );

      // Save to localStorage
      localStorage.setItem("userJobs", JSON.stringify(updatedJobs));
      console.log("✅ Job updated in localStorage:", jobId);

      return updatedJobs;
    });
  };

  const removeJob = (jobId: string) => {
    setMyJobs((prev) => {
      const updatedJobs = prev.filter((job) => job.id !== jobId);

      // Save to localStorage
      localStorage.setItem("userJobs", JSON.stringify(updatedJobs));
      console.log("🗑️ Job removed from localStorage:", jobId);

      return updatedJobs;
    });
  };

  // FIXED: Fetch my jobs with proper 304 handling
  const fetchMyJobs = async (): Promise<void> => {
    if (!user) return;

    try {
      console.log("💼 Fetching user jobs from server...");
      const data = await apiRequest("/jobs/user/my-jobs");

      // Handle 304 response - use cached data
      if (data?.fromCache) {
        console.log("🔄 Using cached jobs data");
        const localJobs = localStorage.getItem("userJobs");
        if (localJobs) {
          try {
            const parsedJobs = JSON.parse(localJobs);
            setMyJobs(Array.isArray(parsedJobs) ? parsedJobs : []);
            console.log("📋 Using cached jobs:", parsedJobs.length);
            return;
          } catch (parseError) {
            console.error("❌ Error parsing cached jobs:", parseError);
          }
        }
      }

      // FIX: Properly extract jobs from response structure
      let jobsData = [];

      if (data?.data?.jobs) {
        // New structure: data.data.jobs
        jobsData = data.data.jobs;
      } else if (data?.jobs) {
        // Alternative structure: data.jobs
        jobsData = data.jobs;
      } else if (Array.isArray(data)) {
        // Direct array
        jobsData = data;
      }

      const serverJobs = Array.isArray(jobsData) ? jobsData : [];

      console.log("📊 Server jobs count:", serverJobs.length);

      // Check localStorage for any locally stored jobs
      const localJobs = localStorage.getItem("userJobs");
      let finalJobs = serverJobs;

      if (localJobs) {
        try {
          const parsedLocalJobs = JSON.parse(localJobs);
          console.log("💾 Local jobs found:", parsedLocalJobs.length);

          // Merge server and local jobs, removing duplicates
          const mergedJobs = [...serverJobs];
          const serverJobIds = new Set(serverJobs.map((j: Job) => j.id));

          parsedLocalJobs.forEach((localJob: Job) => {
            if (!serverJobIds.has(localJob.id)) {
              mergedJobs.push(localJob);
            }
          });

          finalJobs = mergedJobs;
          console.log("🔄 Merged jobs count:", finalJobs.length);
        } catch (parseError) {
          console.error("❌ Error parsing local jobs:", parseError);
          localStorage.removeItem("userJobs");
        }
      }

      setMyJobs(finalJobs);
      localStorage.setItem("userJobs", JSON.stringify(finalJobs));
      console.log("✅ Final jobs saved to localStorage:", finalJobs.length);
    } catch (err) {
      console.error("❌ Failed to fetch jobs from server:", err);

      // Enhanced error handling for 304
      if (err instanceof Error && err.message?.includes("304")) {
        console.log("🔄 304 - Using locally cached jobs");
        const localJobs = localStorage.getItem("userJobs");
        if (localJobs) {
          try {
            const parsedJobs = JSON.parse(localJobs);
            setMyJobs(Array.isArray(parsedJobs) ? parsedJobs : []);
            return;
          } catch (parseError) {
            console.error("❌ Error parsing cached jobs:", parseError);
          }
        }
      }

      // Fallback to localStorage
      const localJobs = localStorage.getItem("userJobs");
      if (localJobs) {
        try {
          const parsedJobs = JSON.parse(localJobs);
          setMyJobs(Array.isArray(parsedJobs) ? parsedJobs : []);
          console.log("📋 Using local jobs as fallback:", parsedJobs.length);
        } catch (parseError) {
          console.error("❌ Error parsing fallback jobs:", parseError);
          setMyJobs([]);
        }
      } else {
        setMyJobs([]);
      }
    }
  };

  // NEW: Get job statistics
  const getJobStats = () => {
    const now = new Date();
    const activeJobs = myJobs.filter((job) => {
      if (!job.deadline) return true;
      const deadline = new Date(job.deadline);
      return deadline > now;
    });

    const draftJobs = myJobs.filter(
      (job) => !job.createdAt || job.createdAt === "draft",
    );

    return {
      total: myJobs.length,
      active: activeJobs.length,
      draft: draftJobs.length,
    };
  };

  // NEW: Clear all jobs (useful for logout or account cleanup)
  const clearAllJobs = () => {
    setMyJobs([]);
    localStorage.removeItem("userJobs");
    console.log("🧹 All jobs cleared from localStorage");
  };

  // NEW: Resend email verification
  const resendVerificationEmail = async (email: string): Promise<void> => {
    setLoading(true);
    clearError();

    try {
      await apiRequest("/auth/resend-verification", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    } catch (error: unknown) {
      handleError(error, "Failed to resend verification email");
    } finally {
      setLoading(false);
    }
  };

  // NEW: Verify email with token
  const verifyEmail = async (token: string): Promise<boolean> => {
    setLoading(true);
    clearError();

    try {
      const response = await apiRequest<{ verified: boolean }>(
        "/auth/verify-email",
        {
          method: "POST",
          body: JSON.stringify({ token }),
        },
      );

      if (response.verified && user) {
        // Update user's email verification status
        updateUser({ emailVerified: true });
      }

      return response.verified;
    } catch (error: unknown) {
      handleError(error, "Failed to verify email");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Enhanced updateUserProfile function with comprehensive status checking
  const updateUserProfile = async (profileData: {
    name?: string;
    email?: string;
    phone?: string;
    location?: string;
    bio?: string;
    avatar?: string;
  }): Promise<{ success: boolean; message?: string }> => {
    setLoading(true);
    clearError();

    try {
      console.log("🔄 Starting profile update process...", profileData);

      // First check if user status is valid with enhanced checking
      const statusCheck = await checkAndRefreshUserStatus();

      if (!statusCheck.isValid) {
        const errorMessage =
          statusCheck.message || "Please log in again to refresh your session.";

        if (statusCheck.needsReauth) {
          setError({ message: errorMessage });
          throw new Error(errorMessage);
        } else {
          // For non-reauth errors, show warning but continue
          console.warn("⚠️ Status check warning:", statusCheck.message);
        }
      }

      console.log("✅ User status valid, proceeding with profile update...");

      // Validate email if being changed
      if (profileData.email && profileData.email !== user?.email) {
        console.log("📧 Email change detected, validating new email...");

        const emailStatus = await checkAccountStatus(profileData.email);
        if (emailStatus.exists && emailStatus.active) {
          throw new Error(
            "This email is already associated with an active account.",
          );
        }
      }

      // Perform the profile update
      const response = await apiRequest<{ user: User }>("/auth/profile", {
        method: "PUT",
        body: JSON.stringify(profileData),
      });

      console.log("✅ Profile update successful:", response);

      // CRITICAL FIX: Properly update state with backend response
      setUser((prev) => {
        if (!prev) return null;

        const profileDataFromResponse =
          response.user?.profiles || response.user?.profile || {};

        const updatedUser = {
          ...prev,
          // Update basic user fields
          name: response.user?.name || prev.name,
          email: response.user?.email || prev.email,
          // Update profile data - handle both 'profile' and 'profiles' for compatibility
          profiles: {
            ...prev.profiles,
            ...profileDataFromResponse,
          },
        };

        // Update localStorage
        localStorage.setItem("user", JSON.stringify(updatedUser));
        console.log(
          "✅ User data updated in localStorage and state:",
          updatedUser,
        );

        return updatedUser;
      });

      return {
        success: true,
        message: "Profile updated successfully!",
      };
    } catch (error: unknown) {
      console.error("❌ Profile update failed:", error);

      let errorMessage = "Failed to update profile";

      if (error instanceof Error) {
        if (error.message.includes("already associated")) {
          errorMessage = error.message;
        } else if (error.message.includes("deactivated")) {
          errorMessage = "Cannot update profile: Account is deactivated.";
        } else if (
          error.message.includes("session") ||
          error.message.includes("log in")
        ) {
          errorMessage = "Your session has expired. Please log in again.";
        } else {
          errorMessage = error.message;
        }
      }

      handleError(error, errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  // Enhanced error handler with better distinction between auth and authz errors
  const handleError = (error: unknown, defaultMessage: string) => {
    console.error(`${defaultMessage}:`, error);

    // Check for deactivated account errors
    if (error instanceof Error && error.message.includes("deactivated")) {
      setError({
        message:
          "Your account has been deactivated. Please request reactivation.",
        code: "ACCOUNT_DEACTIVATED",
      });
      // Clear local storage and logout
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("userBookmarks");
      localStorage.removeItem("userJobs");
      setUser(null);
      setBookmarks([]);
      setMyJobs([]);
      return;
    }

    // Check for token-related errors - ONLY logout for actual authentication errors
    if (error instanceof Error && isAuthenticationError(error)) {
      console.log("🔐 Authentication error detected, cleaning up...");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
      setError({ message: "Your session has expired. Please log in again." });
      return;
    }

    // Check for rate limiting in error message
    if (error instanceof Error) {
      if (
        error.message?.includes("Too many") ||
        error.message?.includes("429") ||
        error.message?.includes("rate limit")
      ) {
        setError({
          message: "Too many requests. Please wait a moment and try again.",
        });
        setRateLimited(true);

        // Extract retry-after time if available
        const retryMatch = error.message.match(/retry after[:\s]*(\d+)/i);
        if (retryMatch) {
          setRetryAfter(parseInt(retryMatch[1]));
        } else {
          setRetryAfter(5); // Default 5 seconds
        }
      } else {
        // For non-authentication errors, just show the error without logging out
        setError({ message: error.message });
      }
    } else if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      setError({
        message: "Network error. Please check your connection and try again.",
      });
    } else {
      setError({ message: defaultMessage });
    }

    throw error;
  };

  // FIXED: Relaxed token validation - only checks basic format, not expiration
  const isTokenValid = (): boolean => {
    // Try multiple token sources
    const token = localStorage.getItem("token") || user?.token;

    if (!token) {
      console.log("❌ No token found");
      return false;
    }

    try {
      // Basic JWT format check only - don't check expiration
      const parts = token.split(".");
      if (parts.length !== 3) {
        console.log("❌ Invalid token format");
        return false;
      }

      console.log("✅ Token exists and has valid format");
      return true;
    } catch (error) {
      console.error("❌ Error validating token format:", error);
      return false;
    }
  };

  // Refresh token function
  const refreshToken = async (): Promise<boolean> => {
    const token = localStorage.getItem("token");
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE}/auth/refresh-token`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const newToken = data.token;

        localStorage.setItem("token", newToken);

        // Update user with new token
        setUser((prev) => (prev ? { ...prev, token: newToken } : null));

        return true;
      } else {
        throw new Error("Token refresh failed");
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      logout();
      return false;
    }
  };

  // FIXED: Generic API request helper with proper 304 handling
  const apiRequest = async <T = any,>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0,
  ): Promise<T> => {
    // Get token from multiple sources with priority FIRST
    let finalToken = localStorage.getItem("token") || user?.token;
    if (!finalToken && user?.token) {
      finalToken = user.token;
    }

    // DEBUG: Enhanced token debugging (BEFORE validation)
    console.log("🔐 Token Debug - Before Request:", {
      endpoint,
      hasToken: !!finalToken,
      tokenSource: finalToken
        ? localStorage.getItem("token")
          ? "localStorage"
          : "userState"
        : "none",
      userRole: user?.role,
      isAdmin: user?.role === "admin",
      tokenLength: finalToken?.length,
    });

    // Define public endpoints that DON'T need authentication
    const isPublicEndpoint =
      endpoint.includes("/auth/login") ||
      endpoint.includes("/auth/register") ||
      endpoint.includes("/auth/forgot-password") ||
      endpoint.includes("/auth/reset-password") ||
      endpoint.includes("/auth/verify-email") ||
      endpoint.includes("/auth/reactivate") ||
      endpoint.includes("/auth/check-account-status");

    const defaultHeaders: HeadersInit = {
      "Content-Type": "application/json",
    };

    // FIXED: ALWAYS add token for non-public endpoints (including admin endpoints)
    if (!isPublicEndpoint) {
      if (finalToken) {
        defaultHeaders["Authorization"] = `Bearer ${finalToken}`;
        console.log("✅ Added Authorization header for endpoint:", endpoint);
      } else {
        console.warn(
          "⚠️ Making request without token to protected endpoint:",
          endpoint,
        );
        // Don't throw here - let the server return 401
      }
    }

    try {
      // Enhanced request logging
      console.log("🚀 Making API Request:", {
        url: `${API_BASE}${endpoint}`,
        method: options.method || "GET",
        headers: {
          ...defaultHeaders,
          // Don't log the actual token for security
          hasAuthHeader: !!defaultHeaders["Authorization"],
          authHeaderLength: defaultHeaders["Authorization"]?.length || 0,
        },
        hasToken: !!finalToken,
        tokenLength: finalToken?.length,
        userRole: user?.role,
      });

      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      });

      console.log("📨 Response Received:", {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        ok: response.ok,
      });

      // FIXED: Handle 304 Not Modified responses properly
      if (response.status === 304) {
        console.log("🔄 304 Not Modified - returning cached data structure");

        // Reset rate limit state
        setRateLimited(false);
        setRetryAfter(0);

        // Try to get cached data from localStorage based on endpoint
        let cachedData = null;
        try {
          if (endpoint.includes("/bookmarks/my-bookmarks")) {
            const localBookmarks = localStorage.getItem("userBookmarks");
            if (localBookmarks) {
              const bookmarksArray = JSON.parse(localBookmarks);
              cachedData = {
                success: true,
                data: {
                  bookmarks: bookmarksArray,
                  pagination: {
                    currentPage: 1,
                    totalPages: 1,
                    totalBookmarks: bookmarksArray.length,
                    hasNext: false,
                    hasPrev: false,
                    pageSize: 10,
                  },
                },
                code: "BOOKMARKS_FETCHED",
                fromCache: true,
              };
            }
          } else if (endpoint.includes("/user/my-jobs")) {
            const localJobs = localStorage.getItem("userJobs");
            if (localJobs) {
              const jobsArray = JSON.parse(localJobs);
              cachedData = {
                success: true,
                data: {
                  jobs: jobsArray,
                  pagination: {
                    currentPage: 1,
                    totalPages: 1,
                    totalJobs: jobsArray.length,
                    hasNext: false,
                    hasPrev: false,
                    pageSize: 10,
                  },
                  postingStatus: {
                    canPostJobs: true,
                    reason: "Cached data",
                    remainingSlots: null,
                  },
                },
                code: "USER_JOBS_FETCHED",
                fromCache: true,
              };
            }
          }
        } catch (cacheError) {
          console.error("Error reading cached data:", cacheError);
        }

        // Return cached data or default success response
        return (cachedData || { success: true, fromCache: true }) as T;
      }

      // Handle 403 Forbidden with better error distinction
      if (response.status === 403) {
        const errorData = await response.json().catch(() => ({}));

        // Handle deactivated account in API responses
        if (errorData.code === "ACCOUNT_DEACTIVATED") {
          console.error("❌ Account deactivated, logging out...");
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("userBookmarks");
          localStorage.removeItem("userJobs");
          setUser(null);
          setBookmarks([]);
          setMyJobs([]);
          throw new Error(
            "Your account has been deactivated. Please request reactivation.",
          );
        }

        // Only logout for actual token authentication errors
        if (
          errorData.code === "INVALID_TOKEN" ||
          errorData.code === "TOKEN_EXPIRED" ||
          (errorData.error?.includes("token") &&
            !errorData.error?.includes("permission") &&
            !errorData.error?.includes("access"))
        ) {
          console.error("❌ Token authentication error, logging out...");
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("userBookmarks");
          localStorage.removeItem("userJobs");
          setUser(null);
          setBookmarks([]);
          setMyJobs([]);
          throw new Error("Your session has expired. Please log in again.");
        } else {
          // This is a permission/authorization error, not a token error
          throw new Error(
            errorData.error ||
              "You do not have permission to access this resource.",
          );
        }
      }

      // Handle 401 Unauthorized - these are always authentication errors
      if (response.status === 401) {
        const errorData = await response.json().catch(() => ({}));
        if (
          errorData.code === "TOKEN_EXPIRED" ||
          errorData.code === "MISSING_TOKEN"
        ) {
          console.error("❌ Token expired or missing, logging out...");
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("userBookmarks");
          localStorage.removeItem("userJobs");
          setUser(null);
          setBookmarks([]);
          setMyJobs([]);
          throw new Error("Session expired. Please log in again.");
        }
      }

      // Handle rate limiting (429 status code)
      if (response.status === 429) {
        const retryAfter = response.headers.get("Retry-After");
        const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 2000; // Default 2 seconds

        console.log(`Rate limited, waiting ${waitTime}ms...`);

        // If we haven't exceeded retry count, wait and retry
        if (retryCount < 2) {
          await new Promise((resolve) => setTimeout(resolve, waitTime));
          return apiRequest<T>(endpoint, options, retryCount + 1);
        } else {
          throw new Error(
            `Rate limited. Please try again in ${waitTime / 1000} seconds.`,
          );
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.error || `Request failed with status ${response.status}`;

        // Check for rate limiting in error response
        if (
          response.status === 429 ||
          errorMessage.includes("Too many") ||
          errorMessage.includes("rate limit")
        ) {
          throw new Error(`Rate limited: ${errorMessage}`);
        }

        throw new Error(errorMessage);
      }

      // Reset rate limit state on successful request
      setRateLimited(false);
      setRetryAfter(0);

      return response.json();
    } catch (error: any) {
      console.error("💥 API Request Error:", error);

      // Handle network errors with retry
      if (
        error instanceof TypeError &&
        error.message.includes("Failed to fetch")
      ) {
        if (retryCount < 2) {
          console.log("Network error, retrying...");
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return apiRequest<T>(endpoint, options, retryCount + 1);
        }
      }
      throw error;
    }
  };

  // NEW: User refresh function
  const refreshUser = async (): Promise<void> => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser((prev) => (prev ? { ...prev, ...userData } : userData));

        // Update localStorage
        const currentUser = localStorage.getItem("user");
        if (currentUser) {
          const parsedUser = JSON.parse(currentUser);
          const updatedUser = { ...parsedUser, ...userData };
          localStorage.setItem("user", JSON.stringify(updatedUser));
        }
      }
    } catch (error) {
      console.error("Error refreshing user:", error);
    }
  };

  // NEW: Admin functions
  const adminDeactivateUser = async (userId: string): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      await apiRequest(`/auth/admin/users/${userId}/deactivate`, {
        method: "PATCH",
      });

      // Success handling - you might want to refresh user list or show notification
      console.log("User deactivated successfully");
    } catch (error: unknown) {
      handleError(error, "Failed to deactivate user");
    } finally {
      setLoading(false);
    }
  };

  const adminReactivateUser = async (userId: string): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      await apiRequest(`/auth/admin/users/${userId}/reactivate`, {
        method: "PATCH",
      });

      console.log("User reactivated successfully");
    } catch (error: unknown) {
      handleError(error, "Failed to reactivate user");
    } finally {
      setLoading(false);
    }
  };

  // NEW: Super admin functions
  const promoteToSuperAdmin = async (userId: string): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      await apiRequest(`/auth/admin/users/${userId}/promote-to-super-admin`, {
        method: "PATCH",
      });

      console.log("User promoted to super admin successfully");
    } catch (error: unknown) {
      handleError(error, "Failed to promote user to super admin");
    } finally {
      setLoading(false);
    }
  };

  const adminDeactivateAdmin = async (userId: string): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      await apiRequest(`/auth/admin/users/${userId}/deactivate-admin`, {
        method: "PATCH",
      });

      console.log("Admin deactivated successfully");
    } catch (error: unknown) {
      handleError(error, "Failed to deactivate admin");
    } finally {
      setLoading(false);
    }
  };

  // NEW: Account reactivation functions
  const requestAccountReactivation = async (email: string): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      await apiRequest("/auth/reactivate-request", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    } catch (error: unknown) {
      handleError(error, "Failed to request account reactivation");
    } finally {
      setLoading(false);
    }
  };

  const completeAccountReactivation = async (
    token: string,
    newPassword: string,
  ): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      const data = await apiRequest<{ user: any; token: string }>(
        "/auth/reactivate-complete",
        {
          method: "POST",
          body: JSON.stringify({ token, newPassword }),
        },
      );

      // Automatically log the user in after successful reactivation
      const userData: User = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        role: data.user.role,
        isWhitelisted: data.user.isWhitelisted || false,
        subscribed: data.user.subscribed || false,
        emailVerified: data.user.emailVerified || false,
        active: true, // Account is now active
        token: data.token,
        createdAt: data.user.createdAt ?? "",
        profiles: data.user.profiles ?? {},
        subscription: data.user.subscription,
        whitelistSubscription: data.user.whitelistSubscription,
        jobPostingSubscription: data.user.jobPostingSubscription,
        preferences: data.user.preferences || {},
        activePackageType: data.user.activePackageType,
        subscriptionState: data.user.subscriptionState,
        profile: undefined,
        firstName: undefined,
        lastName: undefined,
      };

      setUser(userData);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(userData));

      // Apply user's dark mode preference if available
      if (data.user.preferences?.darkMode !== undefined) {
        toggleDarkMode(data.user.preferences.darkMode);
      }

      // Sync subscriptions after reactivation
      try {
        await syncUserSubscriptions(userData.id, data.token);
      } catch (syncError) {
        console.error("Subscription sync failed on reactivation:", syncError);
      }

      // Fetch bookmarks and jobs after reactivation
      try {
        await fetchBookmarks();
        await fetchMyJobs();
        console.log("✅ Bookmarks and jobs loaded after reactivation");
      } catch (fetchError) {
        console.error(
          "❌ Failed to fetch bookmarks/jobs on reactivation:",
          fetchError,
        );
      }
    } catch (error: unknown) {
      handleError(error, "Failed to complete account reactivation");
    } finally {
      setLoading(false);
    }
  };

  // Enhanced verifyResetToken with the improved apiRequest
  const verifyResetToken = async (token: string): Promise<boolean> => {
    try {
      const data = await apiRequest<{ valid: boolean }>(
        "/auth/verify-reset-token",
        {
          method: "POST",
          body: JSON.stringify({ token }),
        },
      );
      return data.valid === true;
    } catch (error: any) {
      console.error("Token verification failed:", error);
      return false;
    }
  };

  const updateUser = (userData: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return null;

      const updatedUser = {
        ...prev,
        ...userData,
        profiles: {
          ...prev.profiles,
          ...(userData.profiles || {}),
        },
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  // Enhanced subscription management functions
  const updateSubscription = (subscription: Subscription) => {
    setUser((prev) => {
      if (!prev) return null;

      let updatedUser: User;

      if (subscription.planType === "whitelist") {
        updatedUser = {
          ...prev,
          isWhitelisted: subscription.isActive,
          whitelistSubscription: subscription,
          subscribed: true, // User is subscribed if they have any active subscription
        };
      } else {
        updatedUser = {
          ...prev,
          subscribed: subscription.isActive,
          jobPostingSubscription: subscription,
        };
      }

      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  const updateWhitelistStatus = (isWhitelisted: boolean) => {
    setUser((prev) => {
      if (!prev) return null;

      const updatedUser: User = {
        ...prev,
        isWhitelisted,
        // If user becomes whitelisted, they should be able to post jobs
        subscribed: isWhitelisted ? true : prev.subscribed,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  const setJobPostingSubscription = (subscription: Subscription | null) => {
    setUser((prev) => {
      if (!prev) return null;

      const updatedUser: User = {
        ...prev,
        jobPostingSubscription: subscription || undefined,
        // User is subscribed if they have either whitelist OR job posting subscription
        subscribed: !!(subscription || prev.whitelistSubscription),
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  const setWhitelistSubscription = (subscription: Subscription | null) => {
    setUser((prev) => {
      if (!prev) return null;

      const updatedUser: User = {
        ...prev,
        whitelistSubscription: subscription || undefined,
        isWhitelisted: !!subscription?.isActive,
        // User is subscribed if they have either whitelist OR job posting subscription
        subscribed: !!(subscription || prev.jobPostingSubscription),
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  // NEW: Subscription state management APIs
  const fetchSubscriptionState = async (): Promise<void> => {
    if (!user) return;

    try {
      const data = await apiRequest("/payments/subscription/state");

      setUser((prev) => {
        if (!prev) return null;

        const updatedUser = {
          ...prev,
          subscriptionState: data,
          subscribed: data.user.subscribed,
          activePackageType: data.user.activePackageType,
          isWhitelisted: data.subscriptions.isWhitelisted,
        };

        localStorage.setItem("user", JSON.stringify(updatedUser));
        return updatedUser;
      });
    } catch (error) {
      console.error("Failed to fetch subscription state:", error);
      throw error;
    }
  };

  const syncSubscriptionState = async (): Promise<void> => {
    if (!user) return;

    try {
      // Use the syncUserSubscriptions function that was previously unused
      const data = await syncUserSubscriptions(user.id, user.token);

      // Update local state with synced data
      setUser((prev) => {
        if (!prev) return null;

        const updatedUser = {
          ...prev,
          subscriptionState: data,
          subscribed: data.user.subscribed,
          activePackageType: data.user.activePackageType,
          isWhitelisted: data.subscriptions.isWhitelisted,
        };

        localStorage.setItem("user", JSON.stringify(updatedUser));
        return updatedUser;
      });

      return data;
    } catch (error) {
      console.error("Failed to sync subscription state:", error);
      throw error;
    }
  };

  const getSubscriptionBenefits = async (): Promise<any> => {
    if (!user) throw new Error("User not authenticated");

    try {
      const data = await apiRequest("/payments/subscription/benefits");
      return data;
    } catch (error) {
      console.error("Failed to fetch subscription benefits:", error);
      throw error;
    }
  };

  const trackJobPostingUsage = async (
    jobId: string,
    action = "create",
  ): Promise<any> => {
    if (!user) throw new Error("User not authenticated");

    try {
      const data = await apiRequest("/payments/job-posting/track-usage", {
        method: "POST",
        body: JSON.stringify({ jobId, action }),
      });

      // Update local subscription state with new usage data
      await fetchSubscriptionState();

      return data;
    } catch (error) {
      console.error("Failed to track job posting usage:", error);
      throw error;
    }
  };

  // Enhanced helper methods to check subscription status
  const hasActiveJobPostingSubscription = (): boolean => {
    if (!user) return false;

    // Use subscription state if available
    if (user.subscriptionState) {
      return user.subscriptionState.subscriptions.hasJobPosting;
    }

    // Fallback to legacy logic
    return (
      user.role === "admin" ||
      user.isWhitelisted ||
      !!user.jobPostingSubscription?.isActive ||
      user.subscribed
    );
  };

  const hasActiveWhitelistSubscription = (): boolean => {
    if (!user) return false;

    // Use subscription state if available
    if (user.subscriptionState) {
      return user.subscriptionState.subscriptions.isWhitelisted;
    }

    // Fallback to legacy logic - FIXED: Added !! to convert undefined to false
    return user.isWhitelisted || !!user.whitelistSubscription?.isActive;
  };

  const canPostJobs = (): boolean => {
    if (!user) return false;

    // Use subscription state if available
    if (user.subscriptionState) {
      return user.subscriptionState.capabilities.canPostJobs;
    }

    // Fallback to legacy logic
    return (
      user.role === "admin" ||
      user.isWhitelisted ||
      !!user.jobPostingSubscription?.isActive ||
      user.subscribed
    );
  };

  const getRemainingJobPosts = (): number => {
    if (!user) return 0;

    // Use subscription state if available
    if (user.subscriptionState) {
      return user.subscriptionState.capabilities.remainingJobPosts;
    }

    // Fallback logic
    if (user.role === "admin") return 999;
    if (user.isWhitelisted) return 999;

    const maxPosts = user.jobPostingSubscription?.maxJobPosts || 0;
    const usedPosts = user.jobPostingSubscription?.jobsPosted || 0;
    return Math.max(0, maxPosts - usedPosts);
  };

  // Update dark mode function
  const toggleDarkMode = (enabled: boolean) => {
    setIsDark(enabled);

    // Update DOM
    document.documentElement.classList.toggle("dark", enabled);

    // Save to localStorage
    const savedPreferences = localStorage.getItem("userPreferences");
    const preferences = savedPreferences ? JSON.parse(savedPreferences) : {};

    const updatedPreferences = {
      ...preferences,
      darkMode: enabled,
    };

    localStorage.setItem("userPreferences", JSON.stringify(updatedPreferences));

    // Also update user profile if logged in
    if (user) {
      // You might want to sync this with the backend
      const userData = localStorage.getItem("user");
      if (userData) {
        const parsedUser = JSON.parse(userData);
        const updatedUser = {
          ...parsedUser,
          preferences: updatedPreferences,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
    }
  };

  // Account deletion function
  const deleteAccount = async (password: string): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      await apiRequest("/auth/account", {
        method: "DELETE",
        body: JSON.stringify({ password }),
      });

      // Clear local state and storage on successful deletion
      logout();
    } catch (error: unknown) {
      handleError(error, "Failed to delete account");
    } finally {
      setLoading(false);
    }
  };

  // Account deactivation functions
  const deactivateAccount = async (password: string): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      await apiRequest("/auth/deactivate", {
        method: "POST",
        body: JSON.stringify({ password }),
      });

      // Clear local state and storage on successful deactivation
      logout();
    } catch (error: unknown) {
      // Enhanced rate limit handling
      if (error instanceof Error && error.message.includes("Rate limited")) {
        const retryAfter = extractRetryAfter(error.message) || 60;
        setRateLimited(true);
        setRetryAfter(retryAfter);
        setError({
          message: `Too many deactivation attempts. Please try again in ${retryAfter} seconds.`,
          code: "RATE_LIMITED",
          retryAfter,
        });
        return;
      }
      handleError(error, "Failed to deactivate account");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to extract retry time
  const extractRetryAfter = (message: string): number | null => {
    const match = message.match(/try again in (\d+) seconds?/i);
    return match ? parseInt(match[1]) : null;
  };

  // Authentication functions
  const register = async (name: string, email: string, password: string) => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      const data = await apiRequest("/auth/register", {
        method: "POST",
        body: JSON.stringify({ name, email, password }),
      });
      console.log("Registration successful:", data);
      // After successful registration, automatically log the user in
      await login(email, password);
    } catch (error: unknown) {
      handleError(error, "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const login = async (
    email: string,
    password: string,
    headers: Record<string, string> = {},
  ): Promise<LoginResult> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      // FIRST: Check if this email belongs to a deactivated account
      try {
        const deactivationCheck = await apiRequest(
          '/auth/check-account-status',
          {
            method: 'POST',
            body: JSON.stringify({ email }),
          },
        );

        if (deactivationCheck?.deactivated) {
          return {
            success: false,
            accountDeactivated: true,
            deactivatedAt: deactivationCheck.deactivatedAt,
            deactivationType: deactivationCheck.deactivationType,
            canBeReactivated: deactivationCheck.canBeReactivated,
            error: new Error('Account deactivated'),
          };
        }
      } catch (checkError) {
        // Continue with normal login if check fails
        console.log(
          'Account status check failed, proceeding with login',
          checkError,
        );
      }

      const data = await apiRequest<{ user: any; token: string }>(
        '/auth/login',
        {
          method: 'POST',
          headers: {
            ...headers,
          },
          body: JSON.stringify({ email, password }),
        },
      );

      // Handle successful login
      const userData: User = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        role: data.user.role,
        isWhitelisted: data.user.isWhitelisted || false,
        subscribed: data.user.subscribed || false,
        emailVerified: data.user.emailVerified || false,
        active: data.user.active !== false,
        token: data.token,
        createdAt: data.user.createdAt ?? '',
        profiles: data.user.profiles ?? {},
        subscription: data.user.subscription,
        whitelistSubscription: data.user.whitelistSubscription,
        jobPostingSubscription: data.user.jobPostingSubscription,
        preferences: data.user.preferences || {},
        activePackageType: data.user.activePackageType,
        subscriptionState: data.user.subscriptionState,
        profile: undefined,
        firstName: undefined,
        lastName: undefined,
      };

      setUser(userData);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Clear any stored lock information on successful login
      clearLockStorage();

      // Apply user's dark mode preference if available
      if (data.user.preferences?.darkMode !== undefined) {
        toggleDarkMode(data.user.preferences.darkMode);
      }

      // Sync subscriptions after successful login
      try {
        await syncUserSubscriptions(userData.id, data.token);
      } catch (syncError) {
        console.error('Subscription sync failed on login:', syncError);
      }

      // Enhanced bookmark and job fetching with localStorage integration
      try {
        await fetchBookmarks();
        await fetchMyJobs();
        console.log('✅ Bookmarks and jobs loaded successfully after login');

        // Get stats for debugging
        const bookmarkStats = getBookmarkStats();
        const jobStats = getJobStats();
        console.log('📊 Stats after login:', { bookmarkStats, jobStats });
      } catch (fetchError) {
        console.error(
          '❌ Failed to fetch bookmarks/jobs on login:',
          fetchError,
        );
        // Don't fail login if bookmarks/jobs fail
      }

      return {
        success: true,
      };
    } catch (error: unknown) {
      console.log('Login error details:', error);

      // ENHANCED: Better deactivated account detection
      if (isApiError(error)) {
        // Handle account deactivated specifically
        if (error.code === 'ACCOUNT_DEACTIVATED') {
          return {
            success: false,
            accountDeactivated: true,
            deactivatedAt: error.deactivatedAt,
            deactivationType: error.deactivationType,
            canBeReactivated: true,
            error,
          };
        }

        // Handle account lock specifically
        if (error.code === 'ACCOUNT_LOCKED') {
          setRateLimited(true);
          const retryAfter = error.retryAfter || 1800;

          // Store lock information in localStorage for persistence
          const lockUntil = new Date(
            Date.now() + retryAfter * 1000,
          ).toISOString();
          localStorage.setItem('accountLockedUntil', lockUntil);

          if (error.remainingAttempts !== undefined) {
            localStorage.setItem(
              'remainingAttempts',
              error.remainingAttempts.toString(),
            );
          }

          const lockStatus = {
            locked: true,
            retryAfter,
            message:
              error.error ||
              `Account temporarily locked. Try again in ${formatTimeRemaining(retryAfter)}.`,
          };

          updateLockStatus(lockStatus);

          return {
            success: false,
            locked: true,
            retryAfter,
            error,
          };
        }

        // Handle remaining attempts
        if (
          error.code === 'INVALID_CREDENTIALS' &&
          error.remainingAttempts !== undefined
        ) {
          const remainingAttempts = error.remainingAttempts;
          let message = error.error || 'Invalid email or password';

          if (
            remainingAttempts > 0 &&
            !message.includes('attempts remaining')
          ) {
            message += ` ${remainingAttempts} attempt${remainingAttempts !== 1 ? 's' : ''} remaining.`;
          }

          setError({
            message,
            code: error.code,
            remainingAttempts,
          });

          localStorage.setItem(
            'remainingAttempts',
            remainingAttempts.toString(),
          );

          updateLockStatus({
            locked: false,
            message: `${remainingAttempts} attempt${remainingAttempts !== 1 ? 's' : ''} remaining before account lock.`,
          });

          return {
            success: false,
            remainingAttempts,
            error,
          };
        }

        // Handle other specific error codes
        if (error.code === 'EMAIL_NOT_VERIFIED') {
          setError({
            message: 'Please verify your email before logging in.',
            code: error.code,
          });
          return {
            success: false,
            requiresVerification: true,
            error,
          };
        }
      }

      // Fallback: Check error message for deactivation clues
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();
        if (
          errorMessage.includes('deactivated') ||
          errorMessage.includes('inactive') ||
          errorMessage.includes('suspended')
        ) {
          return {
            success: false,
            accountDeactivated: true,
            canBeReactivated: true,
            error,
          };
        }
      }

      // Fallback to generic error handling
      const genericError =
        error instanceof Error ? error : new Error('Login failed');
      handleError(genericError, 'Login failed');
      return {
        success: false,
        error: genericError,
      };
    } finally {
      setLoading(false);
    }
  };

  // MODIFIED: Only sync on logout (manual sync)
  const logout = () => {
    console.log("🚪 Logging out user...");

    // Reset rate limiters
    bookmarkRateLimiter.current.reset();
    jobRateLimiter.current.reset();
    syncInProgress.current = false;

    // Clear all data
    clearAllBookmarks();
    clearAllJobs();
    setUser(null);
    setError(null);
    setLoading(false);
    setRateLimited(false);
    setRetryAfter(0);

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("redirectAfterLogin");

    console.log("✅ Logout completed");
  };

  // Password reset functions
  const requestPasswordReset = async (email: string): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      await apiRequest("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    } catch (error: unknown) {
      handleError(error, "Failed to request password reset");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (
    token: string,
    newPassword: string,
  ): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      await apiRequest("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, newPassword }),
      });
    } catch (error: unknown) {
      handleError(error, "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  // Profile management functions
  const updateProfile = async (profileData: {
    name: string;
    email: string;
    phone?: string;
    location?: string;
    bio?: string;
  }): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      const data = await apiRequest<{ user: User }>("/auth/profile", {
        method: "PUT",
        body: JSON.stringify(profileData),
      });

      updateUser(data.user);
    } catch (error: unknown) {
      handleError(error, "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string,
  ): Promise<void> => {
    setLoading(true);
    clearError();
    setRateLimited(false);

    try {
      await apiRequest("/auth/change-password", {
        method: "PUT",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
    } catch (error: unknown) {
      handleError(error, "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  // MODIFIED: Initialize with fetch only, no immediate sync
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("token");
      const userData = localStorage.getItem("user");

      console.log("🔄 Initializing auth:", {
        hasToken: !!token,
        hasUserData: !!userData,
      });

      if (token && userData) {
        try {
          // Use a more lenient token validation for initialization
          const isValid = isTokenValid();

          if (!isValid) {
            console.log("⚠️ Token invalid during init, but continuing...");
            // Don't logout immediately, let the backend handle invalid tokens
          }

          const parsedUser = JSON.parse(userData);

          // Check if account is deactivated
          if (parsedUser.active === false) {
            console.log("❌ Deactivated account found during init");
            setError({
              message:
                "Your account has been deactivated. Please request reactivation.",
              code: "ACCOUNT_DEACTIVATED",
            });
            // Don't logout immediately, let user see the error
            return;
          }

          const validatedUser: User = {
            id: parsedUser.id,
            email: parsedUser.email,
            name: parsedUser.name,
            role: parsedUser.role || "user",
            isWhitelisted: parsedUser.isWhitelisted || false,
            subscribed: parsedUser.subscribed || false,
            emailVerified: parsedUser.emailVerified || false,
            active: parsedUser.active !== false,
            token: token,
            createdAt: parsedUser.createdAt || "",
            profiles: parsedUser.profiles || {},
            subscription: parsedUser.subscription,
            whitelistSubscription: parsedUser.whitelistSubscription,
            jobPostingSubscription: parsedUser.jobPostingSubscription,
            preferences: parsedUser.preferences || {},
            activePackageType: parsedUser.activePackageType,
            subscriptionState: parsedUser.subscriptionState,
            profile: undefined,
            firstName: undefined,
            lastName: undefined,
          };

          setUser(validatedUser);
          console.log("✅ User initialized from localStorage");

          // Apply user's dark mode preference
          if (parsedUser.preferences?.darkMode !== undefined) {
            toggleDarkMode(parsedUser.preferences.darkMode);
          }

          // Try to sync subscriptions, but don't block on errors
          try {
            await syncUserSubscriptions(validatedUser.id, token);
          } catch (syncError) {
            console.warn("⚠️ Subscription sync failed on init:", syncError);
          }

          // Fetch data (but don't sync immediately on load)
          try {
            await fetchBookmarks();
            await fetchMyJobs();
            console.log("✅ Data loaded on initialization");
          } catch (fetchError) {
            console.warn("⚠️ Failed to fetch data on load:", fetchError);
          }
        } catch (error) {
          console.error("❌ Error initializing auth:", error);
          // Don't logout on initialization errors
        }
      } else {
        console.log("ℹ️ No stored auth data found");
      }
    };

    initializeAuth();
  }, []);

  const contextValue: AuthContextType = {
    user,
    bookmarks,
    myJobs,
    login,
    register,
    logout,
    updateUser,
    updateSubscription,
    updateWhitelistStatus,
    setJobPostingSubscription,
    setWhitelistSubscription,
    // Enhanced bookmark management
    addBookmark,
    removeBookmark,
    fetchBookmarks,
    isJobBookmarked,
    getBookmarkStats,
    clearAllBookmarks,
    syncBookmarksToServer,
    // Enhanced job management
    fetchMyJobs,
    addJob,
    updateJob,
    removeJob,
    getJobStats,
    clearAllJobs,
    syncJobsToServer,
    // New subscription state APIs
    fetchSubscriptionState,
    syncSubscriptionState,
    getSubscriptionBenefits,
    trackJobPostingUsage,
    // Enhanced helper methods
    hasActiveJobPostingSubscription,
    hasActiveWhitelistSubscription,
    canPostJobs,
    getRemainingJobPosts,
    // NEW: Account reactivation functions
    requestAccountReactivation,
    completeAccountReactivation,
    // NEW: Account status management
    isAccountActive,
    checkAccountStatus,
    // NEW: Email verification functions
    resendVerificationEmail,
    verifyEmail,
    // UPDATED: Profile update function with enhanced error handling
    updateUserProfile,
    // NEW: User status checking function
    checkAndRefreshUserStatus,
    // NEW: Admin functions
    adminDeactivateUser,
    adminReactivateUser,
    // NEW: Super admin functions
    promoteToSuperAdmin,
    adminDeactivateAdmin,
    // NEW: User refresh function
    refreshUser,
    // NEW: API request function
    apiRequest,
    // Password reset functions
    requestPasswordReset,
    verifyResetToken,
    resetPassword,
    // Profile management
    updateProfile,
    changePassword,
    // Account management
    deleteAccount,
    deactivateAccount,
    // Dark mode
    isDark,
    toggleDarkMode,
    loading,
    error,
    clearError,
    rateLimited,
    retryAfter,
    isTokenValid,
    isAuthenticated: !!user && isTokenValid() && user.active !== false,
    refreshToken,
    // Account lock status management
    lockStatus,
    checkLockStatus,
    clearLockStatus,
    startCountdown,
    countdown,
    updateLockStatus,
    // NEW: Preferences management
    updatePreferences,
    getPreferences,
    savePreferencesToServer,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}

// Export helper function for external use
export { getSubscriptionStatus };
